import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Pass 46 — Compact dashboard pending list.
//
// Replaces the Pass-45 large action cards. Each row is identical in
// shape to the Upcoming Appointments list rows (customer · watch ·
// won date) so the dashboard reads as a single coherent surface, not
// two competing call-to-actions. Clicking a row navigates to the
// leads tab with `?open=<lead_id>` so the parent leads page can
// auto-open that lead's drawer — and the drawer is where the
// salesperson actually composes and sends.
//
// `apiClient` is the existing teamApi or adminApi binding from
// Pass 45. `leadsPath` is the route the rows link into; "/team/leads"
// for the team panel, "/admin/leads" for the admin dashboard.

function formatWonDate(s) {
  const d = (s || "").split(" ")[0];
  if (!d) return "—";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-MY", {
      day: "numeric", month: "short",
    });
  } catch { return d; }
}

export default function ReviewQueue({ apiClient, leadsPath, title = "Review requests pending" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.reviews();
      setItems(r.data?.items || []);
    } catch { /* swallow — section just stays hidden on error */ }
    finally { setLoading(false); }
  }, [apiClient]);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  // Hide the section entirely when empty — keeps the dashboard
  // uncluttered when there's nothing actionable.
  if (items.length === 0) return null;

  return (
    <section data-testid="review-queue" style={{ marginBottom: 32 }}>
      <h2 style={{ fontSize: 18, margin: "0 0 12px 0" }}>
        {title}{" "}
        <span data-testid="review-queue-count" style={{ color: "var(--scw-muted)", fontWeight: 400, fontSize: 14 }}>
          ({items.length})
        </span>
      </h2>
      <div className="scw-card" style={{ padding: 0 }}>
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {items.map((it, idx) => (
            <li
              key={it.lead_id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`${leadsPath}?open=${encodeURIComponent(it.lead_id)}`)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`${leadsPath}?open=${encodeURIComponent(it.lead_id)}`);
                }
              }}
              data-testid={`review-row-${it.lead_id}`}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                padding: "14px 18px",
                borderTop: idx === 0 ? "none" : "1px solid var(--scw-line)",
                cursor: "pointer",
                minHeight: 44,
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {it.customer_name}
                </p>
                <p style={{ margin: "2px 0 0 0", fontSize: 13, color: "var(--scw-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {it.watch}{it.store === "JB" ? " · JB" : ""}
                </p>
              </div>
              <span className="scw-eyebrow" style={{ flex: "0 0 auto" }}>
                Won {formatWonDate(it.won_date)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
